import {
  Button, Card, Input, Select, Space, Table, Typography,
} from 'antd';
import rideKycApi from 'api/rideKycApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRideKycErrorMessage, RideKycStatusTag, STATUS_OPTIONS } from '../../rideKycConstants';

const { Text, Title } = Typography;

const FILTER_STORAGE_KEY = 'rideKycQueueFilter';
const DEFAULT_FILTER = {
  status: 'PENDING_REVIEW', q: '', page: 0, limit: 20,
};

const getInitialFilter = () => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(FILTER_STORAGE_KEY));
    return {
      ...DEFAULT_FILTER,
      ...(saved && typeof saved === 'object' ? saved : {}),
    };
  } catch (_) {
    return DEFAULT_FILTER;
  }
};

function RideKycListScreen() {
  const router = useRouter();
  const [filter, setFilter] = useState(getInitialFilter);
  const [searchText, setSearchText] = useState(() => getInitialFilter().q);
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rideKycApi.getProfiles(filter);
      setProfiles(response?.profiles || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setProfiles([]);
      setTotal(0);
      ToastHelper.showError(getRideKycErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);
  useEffect(() => {
    window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
  }, [filter]);

  const goToDetail = (driverProfileId) => router.navigate(`${driverProfileId}`);

  const columns = useMemo(() => [
    {
      title: 'Mã hồ sơ', dataIndex: 'driverProfileId', width: 110,
      render: (value) => <Text strong>#{value}</Text>,
    },
    {
      title: 'Tài xế', key: 'driver',
      render: (_, row) => (
        <div>
          <div><Text strong>{row?.user?.fullname || 'Chưa có tên'}</Text></div>
          <Text type="secondary">{row?.user?.phone || row?.user?.email || `User #${row?.userId}`}</Text>
        </div>
      ),
    },
    { title: 'Trạng thái', dataIndex: 'status', width: 150, render: (value) => <RideKycStatusTag status={value} /> },
    {
      title: 'CCCD / GPLX', key: 'presence', width: 150,
      render: (_, row) => `${row.identityNumberProvided ? 'Có' : 'Thiếu'} / ${row.licenseNumberProvided ? 'Có' : 'Thiếu'}`,
    },
    {
      title: 'Cập nhật', dataIndex: 'updatedAt', width: 170,
      render: (value) => (value ? new Date(value).toLocaleString('vi-VN') : '—'),
    },
    {
      title: '', key: 'action', width: 110,
      render: (_, row) => <Button type="link" onClick={() => goToDetail(row.driverProfileId)}>Xem hồ sơ</Button>,
    },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Duyệt KYC tài xế xe ghép</Title>
            <Text type="secondary">Tổng {total} hồ sơ theo bộ lọc</Text>
          </div>
          <Button onClick={loadProfiles} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap className="mb-4">
          <Input.Search
            allowClear
            value={searchText}
            placeholder="Tên, email hoặc số điện thoại"
            style={{ width: 320 }}
            onChange={(event) => setSearchText(event.target.value)}
            onSearch={(q) => setFilter((current) => ({ ...current, q: q.trim(), page: 0 }))}
          />
          <Select
            value={filter.status}
            options={STATUS_OPTIONS}
            style={{ width: 190 }}
            onChange={(status) => setFilter((current) => ({ ...current, status, page: 0 }))}
          />
        </Space>
        <Table
          rowKey="driverProfileId"
          loading={loading}
          dataSource={profiles}
          columns={columns}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: filter.page + 1,
            pageSize: filter.limit,
            total,
            showSizeChanger: true,
            onChange: (page, limit) => setFilter((current) => ({ ...current, page: page - 1, limit })),
          }}
          onRow={(row) => ({ onDoubleClick: () => goToDetail(row.driverProfileId) })}
        />
      </Card>
    </div>
  );
}

export default RideKycListScreen;
